import { ApiDateSchema, PhaseTypeSchema } from '@vy/types';
import { oz, vyDate } from '@vy/utils';
import type { VyDate } from '@vy/utils';
import { defined, notDefined } from '@vy/null';

export const NewPhaseFormSchema = oz.object({
  effectiveDate: ApiDateSchema,
  label: PhaseTypeSchema,
  name: oz.string().optional(),
  coTerm: oz.boolean().default(true),
  newContractEndDate: ApiDateSchema.optional(),
});

export function createNewPhaseFormSchemaWithValidation(
  contractStartDate: VyDate,
  contractEndDate?: VyDate,
  lastPhaseEffectiveDate?: VyDate,
) {
  return NewPhaseFormSchema.superRefine((data, ctx) => {
    if (notDefined(data.effectiveDate)) return;

    const newPhaseEffectiveDate = vyDate(data.effectiveDate);

    const isBeforeContractStart = newPhaseEffectiveDate.isSameOrBefore(contractStartDate, 'day');
    if (notDefined(lastPhaseEffectiveDate) && isBeforeContractStart) {
      ctx.addIssue({
        code: oz.ZodIssueCode.custom,
        path: ['effectiveDate'],
        message: 'Effective date must be after the contract start date',
      });
      return;
    }

    const isBeforeLastPhaseEffectiveDate = newPhaseEffectiveDate.isSameOrBefore(
      lastPhaseEffectiveDate,
      'day',
    );
    if (defined(lastPhaseEffectiveDate) && isBeforeLastPhaseEffectiveDate) {
      ctx.addIssue({
        code: oz.ZodIssueCode.custom,
        path: ['effectiveDate'],
        message: 'Effective date must be after the previous phase start date',
      });
      return;
    }

    const isAfterContractEndDate = newPhaseEffectiveDate.isAfter(contractEndDate, 'day');
    if (defined(contractEndDate) && isAfterContractEndDate) {
      ctx.addIssue({
        code: oz.ZodIssueCode.custom,
        path: ['effectiveDate'],
        message: 'Effective date cannot exceed the contract end date',
      });
    }

    if (data.coTerm) {
      return;
    }

    if (notDefined(data.newContractEndDate)) {
      ctx.addIssue({
        code: oz.ZodIssueCode.custom,
        path: ['newContractEndDate'],
        message: 'Select a new contract end date',
      });
      return;
    }

    const newPhaseEndDate = vyDate(data.newContractEndDate);

    const isEndOnOrBeforeEffectiveDate = newPhaseEndDate.isSameOrBefore(newPhaseEffectiveDate, 'day');
    if (isEndOnOrBeforeEffectiveDate) {
      ctx.addIssue({
        code: oz.ZodIssueCode.custom,
        path: ['newContractEndDate'],
        message: 'New contract end date must be after the phase effective date',
      });
      return;
    }

    const isBeforeCurrentContractEndDate = newPhaseEndDate.isBefore(contractEndDate, 'day');
    if (defined(contractEndDate) && isBeforeCurrentContractEndDate) {
      ctx.addIssue({
        code: oz.ZodIssueCode.custom,
        path: ['newContractEndDate'],
        message: 'New contract end date cannot be earlier than the current contract end date',
      });
    }
  });
}

export type NewPhaseForm = oz.infer<typeof NewPhaseFormSchema>;
